import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { Component, Inject, Input } from '@angular/core'
import { from, Subscription } from 'rxjs'
import { TranslateService } from '@ngx-translate/core'
import { BasicFile, BootstrapData, FilesystemFile } from 'elements-sdk'

import { APIService, BOOTSTRAP_DATA } from 'api/module'
import { BaseComponent } from 'common/helpers'
import { MessageBoxService } from 'common/util/services/messageBox.service'
import { HotkeysService } from 'common/util/services/hotkeys.service'
import { IdentityService } from 'main/services/identity.service'
import { NotifyService } from 'common/util/services/notify.service'

import type { OpenDialogOptions } from '../services/openDialog.service'

@Component({
  templateUrl: './openDialogModal.component.pug',
  styleUrls: [
    './openDialogModal.component.scss',
  ],
})
export class OpenDialogModalComponent extends BaseComponent {
  @Input() options: OpenDialogOptions
  wildcards = ['.projects', '.backup']
  root: string
  path: string
  relative?: string
  parent?: string
  previousPath?: string
  missingRoot = false
  loading = false
  loadingCancellable = false
  newFolderName = ''
  showHidden = false
  items: BasicFile[]

  private loadingSubscription?: Subscription

  constructor (
    public identity: IdentityService,
    private modalInstance: NgbActiveModal,
    private messageBox: MessageBoxService,
    private api: APIService,
    private translate: TranslateService,
    private notify: NotifyService,
    @Inject(BOOTSTRAP_DATA) private bootstrapData: BootstrapData,
    hotkeys: HotkeysService,
  ) {
    super()
    this.subs.add(hotkeys.keyup$.subscribe((jsEvent) => {
      if (jsEvent.key === 'Enter') {
        this.selectDir()
      }
    }))
  }

  ngOnInit () {
    this.root = this.options.root ?? this.bootstrapData.settings.DATA_ROOT!
    if (this.options.relative) {
      if (this.options.relative.endsWith('/')) {
        this.options.relative = this.options.relative.slice(0, -1)
      }
      if (this.options.path) {
        this.path = this.options.relative + '/' + this.options.path
      } else {
        this.path = this.options.relative
      }
    } else if (this.options.path) {
      this.path = this.options.path
    } else {
      this.path = this.root
    }

    while (this.path.endsWith('/')) {
      this.path = this.path.substring(0, this.path.length - 1)
    }
    if (!this.identity.hasFSAccess(this.path)) {
      this.path = this.identity.user!.allowedFsPaths![0]
    }
    this.relative = this.options.relative
    this.refresh()
  }

  async refresh () {
    this.items = []
    if (!this.path) {
      return
    }

    this.loading = true
    this.loadingCancellable = false
    setTimeout(() => {
      this.loadingCancellable = true
    }, 1500)

    this.missingRoot = false

    try {
      const request = from(this.api.storage.getFile({
        path: this.path,
        bundle: true,
      }))
      const response: FilesystemFile = await new Promise((resolve, reject) => {
        this.loadingSubscription = request.subscribe({
          next: resolve,
          error: reject,
          complete: () => {
            this.loadingSubscription = undefined
          },
        })
      })
      if (!response.files) {
        this.path = response.parent
        this.refresh()
        return
      }
      this.items = response.files
      this.parent = response.parent
    } catch (e) {
      if ([400, 403, 404].includes(e.status)) {
        // Not a dir
        if (this.canGoBack()) {
          this.goBack()
        } else {
          this.missingRoot = true
        }
      }
    } finally {
      this.loading = false
    }
  }

  cancelLoading () {
    this.loadingSubscription?.unsubscribe()
    this.path = this.previousPath!
    this.refresh()
  }

  async createFolder () {
    const message = await this.messageBox.prompt({
      prompt: this.translate.instant('New directory name'),
    }).catch(() => null)
    const name = message?.value
    if (!name) {
      return
    }

    if (!/^[^\s/]([^/]*)$/.test(name)) {
      this.notify.error(this.translate.instant('Invalid folder name'))
      return
    }
    this.loading = true

    try {
      const newFolder = await this.api.storage.createFile({
        fileUpdate: {
          name,
          parent: this.path,
        },
      })
      this.path = newFolder.path
      this.newFolderName = ''
    } catch (e) {
      this.notify.errorFromResponse(this.translate.instant('Could not create a folder'), e)
    } finally {
      this.refresh()
    }
  }

  close () {
    this.modalInstance.dismiss()
  }

  openItem (item) {
    this.previousPath = this.path
    let path = this.path
    if (!path.endsWith('/')) {
      path += '/'
    }
    path += item.name
    if (item.isDir) {
      this.navigate(path)
    } else {
      this.finish(path)
    }
  }

  navigate (path) {
    this.path = path
    this.refresh()
  }

  selectDir () {
    if (this.options.globMode) {
      let path = this.path
      if (!path.endsWith('/')) {
        path += '/'
      }
      this.finish(path + '*')
    } else {
      this.finish(this.path)
    }
  }

  finish (path) {
    if (this.relative) {
      path = path.substring(this.relative.length + 1)
    }
    this.modalInstance.close(path)
  }

  canGoBack () {
    return this.parent && this.parent !== this.path && this.identity.hasFSAccess(this.parent) && !(
      this.options.confineToRoot && this.root === this.path)
  }

  async goBack () {
    if (this.parent) {
      this.path = this.parent
      await this.refresh()
    }
  }
}
